import React, { useState, useRef, useEffect } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, PointElement, LineElement } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { getBhuMeetPerformanceAPI, getTotalPerformanceOverView } from "../apis";
import Popup from "../../../../ReusableFunctions/Popup";
import LoaderComponent from "../../../../ReusableComponents/LoaderComponent";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { styled } from "@mui/system";
import barChartIcon from '../../../../assets/Bar.png';
import lineChartIcon from '../../../../assets/line.png';

// Register ChartJS components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Tooltip, 
  Legend,
  PointElement,
  LineElement
);

const OverViewGraph = ({ commonFilters ,isDownload}) => {
  const chartRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [overviewData, setOverviewData] = useState([]);
  const [chartType, setChartType] = useState('Bar'); // 'bar' or 'line'

  const getOverViewPerformanceFunc = async () => {
    try {
      setIsLoading(true);
      const bhumeetPerformance = await getTotalPerformanceOverView({ 
        ...commonFilters,
        performanceType: "revenue" 
      });
      setOverviewData(bhumeetPerformance);
      setIsLoading(false);
    } catch (e) {
      Popup.alert("ERROR", "Error while getting performance data.", "ERROR");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (commonFilters) getOverViewPerformanceFunc();
  }, [commonFilters]);

  // Custom color palette
  const colors = {
    upiPayments: {
      bg: 'rgba(242, 114, 111, 1)',
      border: 'rgba(242, 114, 111, 1)'
    },
    offlinePayments: {
      bg: 'rgba(98, 181, 143, 1)',
      border: 'rgba(98, 181, 143, 1)'
    },
    coupons: {
      bg: 'rgba(93, 98, 181, 1)',
      border: 'rgba(93, 98, 181, 1)'
    },
    incentive: {
      bg: 'rgba(248, 178, 113, 1)',
      border: 'rgba(248, 178, 113, 1)'
    },
    platformFees: {
      bg: 'rgba(188, 149, 223, 1)',
      border: 'rgba(188, 149, 223, 1)'
    }
  };

  function formatMonth(monthStr) {
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
}

const handleChangeChartType=(type)=>{
   setChartType(type);
}

  // Prepare chart data
  const chartData = {
    labels: overviewData?.map(item => formatMonth(item.month)),
    datasets: [
      {
        label: 'UPI Payments',
        data: overviewData?.map(item => item.upiPayments),
        backgroundColor: colors.upiPayments.bg,
        borderColor: colors.upiPayments.border,
        borderWidth: chartType === 'Line' ? 2.5 : 1, 
        tension: 0.2, 
        pointBackgroundColor: colors.upiPayments.border,
        pointRadius: chartType === 'Line' ? 4 : 3,
        pointHoverRadius: chartType === 'Line' ? 6 : 5,
        pointBorderWidth: 1,
        fill: false,
        borderRadius: 4,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
      {
        label: 'Cash Payments',
        data: overviewData?.map(item => item.offlinePayments),
        backgroundColor: colors.offlinePayments.bg,
        borderColor: colors.offlinePayments.border,
        borderWidth: chartType === 'Line' ? 2.5 : 1,
        tension: 0.2,
        pointBackgroundColor: colors.offlinePayments.border,
        pointRadius: chartType === 'Line' ? 4 : 3,
        pointHoverRadius: chartType === 'Line' ? 6 : 5,
        pointBorderWidth: 1,
        fill: false,
        borderRadius: 4,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
      {
        label: 'Coupons',
        data: overviewData?.map(item => item.coupons),
        backgroundColor: colors.coupons.bg,
        borderColor: colors.coupons.border,
        borderWidth: chartType === 'Line' ? 2.5 : 1,
        tension: 0.2,
        pointBackgroundColor: colors.coupons.border,
        pointRadius: chartType === 'Line' ? 4 : 3,
        pointHoverRadius: chartType === 'Line' ? 6 : 5,
        pointBorderWidth: 1,
        fill: false,
        borderRadius: 4,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
      {
        label: 'Incentive',
        data: overviewData?.map(item => item.incentive),
        backgroundColor: colors.incentive.bg,
        borderColor: colors.incentive.border,
        borderWidth: chartType === 'Line' ? 2.5 : 1,
        tension: 0.2,
        pointBackgroundColor: colors.incentive.border,
        pointRadius: chartType === 'Line' ? 4 : 3,
        pointHoverRadius: chartType === 'Line' ? 6 : 5,
        pointBorderWidth: 1,
        fill: false,
        borderRadius: 4,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
      {
        label: 'Platform Fees',
        data: overviewData?.map(item => item.platformFees),
        backgroundColor: colors.platformFees.bg,
        borderColor: colors.platformFees.border,
        borderWidth: chartType === 'Line' ? 2.5 : 1,
        tension: 0.2,
        pointBackgroundColor: colors.platformFees.border,
        pointRadius: chartType === 'Line' ? 4 : 3,
        pointHoverRadius: chartType === 'Line' ? 6 : 5,
        pointBorderWidth: 1,
        fill: false,
        borderRadius: 4,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
    ]
  };

  // Update options to better suit line charts
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            family: "'Roboto', sans-serif",
            weight: '500'
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle' // Consistent circle style
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleFont: {
          size: 16,
          weight: 'bold',
          family: "'Roboto', sans-serif"
        },
        bodyFont: {
          size: 13,
          weight: '600',
          family: "'Roboto', sans-serif"
        },
        padding: 16,
        cornerRadius: 8,
        bodySpacing: 10,
        titleSpacing: 8,
        titleMarginBottom: 12,
        boxPadding: 8,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-IN', { 
                style: 'currency', 
                currency: 'INR' 
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12
          }
        },
        offset: true,
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12
          },
          padding: 10,
          callback: function(value) {
            return new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0
            }).format(value);
          }
        },
        beginAtZero: true
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      line: {
        tension: 0.2 
      },
      point: {
        hoverRadius: 6,
        hoverBorderWidth: 1
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuad'
    }
  };


  return (
    <div style={{ 
      width: '100%', 
      maxWidth: "100%",
      margin: '0 auto',
      padding: '20px',
      backgroundColor: '#ffffff',
      borderRadius: '10px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }}>  
      <div style={{
        display: !isDownload?'flex':"none",
        justifyContent: 'flex-end',
        gap:"15px",
        marginBottom: '10px'
      }}>
       <div
       onClick={()=>handleChangeChartType("Bar")} 
       className={`
        ${chartType ==="Bar"?"bg-gray-200 border-none":"border-[1px] border-[rgba(217, 217, 217, 1)]"}
       cursor-pointer
       
       font-manrope text-[12px] font-[600] flex items-center h-8 p-2 gap-1 rounded-[4px]
        `}>
        <span className="mt-1">Bar Chart</span >
        <img src={barChartIcon} alt="bar" width={18} height={18}/>
       </div>
       <div
       onClick={()=>{handleChangeChartType("Line")}} 
       className={`
         ${chartType ==="Line"?"bg-gray-200 border-none":"border-[1px] border-[rgba(217, 217, 217, 1)]"}
       cursor-pointer
       
       font-manrope text-[12px] font-[600] flex items-center h-8 p-2 gap-1 rounded-[4px]
        `}>
        <span className="mt-1">Line Chart</span >
        <img src={lineChartIcon} alt="bar" width={15} height={14}/>
       </div>
      </div>
    

      {isLoading ? (
        <div style={{
          height: '500px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '8px'
        }}>
          <LoaderComponent height="3%" width="3%" /> 
        </div>
      ) :  (
        <div style={{ 
          height: '500px',
          position: 'relative'
        }}>
          {chartType === 'Bar' ? (
            <Bar 
              ref={chartRef} 
              data={chartData} 
              options={options} 
            />
          ) : (
            <Line 
              ref={chartRef} 
              data={chartData} 
              options={options} 
            />
          )}
        </div>
      )}
    </div>
  );
};

export default OverViewGraph;